import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Home, Shield, Mail, Trash2, Phone } from 'lucide-react';

const LOGOS = {
  orange: "/images/logos/orange.svg"
};

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Same style as HomePage */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="text-xl font-bold text-blue-600">Telecomprijsvergelijker<span className="text-orange-500">.be</span></div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
                <img src={LOGOS.orange} alt="Orange" className="h-5" />
                <span className="text-xs font-medium text-orange-700">Officieel Partner</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacybeleid en Algemene Voorwaarden</h1>
            <p className="text-xl text-slate-600">
              Hoe we omgaan met uw persoonlijke gegevens
            </p>
          </div>

          <div className="space-y-8">
            {/* Section 1 */}
            <Card>
              <CardHeader>
                <CardTitle>1. Wie zijn wij?</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Telecomprijsvergelijker.be is een initiatief van:</p>
                <div className="bg-slate-50 p-4 rounded-lg mt-4 space-y-4">
                  <div>
                    <p className="font-semibold">SANDM EXPERTISE COMMV</p>
                    <p>Ondernemingsnummer: BE 0749.886.214</p>
                    <p><strong>Partner-ID:</strong> ATLAS693</p>
                  </div>
                  <div className="border-t pt-4">
                    <p className="font-semibold">VERCRUYSSE WIM</p>
                    <p>Ondernemingsnummer: BE 0808.962.479</p>
                    <p><strong>Partner-ID:</strong> ATLAS630</p>
                  </div>
                </div>
                <p className="mt-4">
                  Beide vennootschappen zijn officieel commercieel partner van Orange en Eneco via distributiekanaal SmartSN.
                </p>
              </CardContent>
            </Card>

            {/* Section 2 */}
            <Card>
              <CardHeader>
                <CardTitle>2. Wat doen wij?</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Telecomprijsvergelijker.be biedt vrijblijvende en kosteloze prijsvergelijkingen aan voor:</p>
                <ul className="mt-2">
                  <li>Telecom - Orange (TV, internet, mobiel)</li>
                  <li>Optioneel: energie (Eneco)</li>
                </ul>
                
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-6">
                  <p className="font-semibold text-blue-900">Belangrijk:</p>
                  <p className="text-blue-800 mt-2">Wij treden uitsluitend op als commercieel tussenpartner en:</p>
                  <ul className="mt-2 text-blue-800">
                    <li>• Leveren zelf geen telecom- of energiediensten</li>
                    <li>• Factureren nooit aan eindklanten</li>
                    <li>• Zijn niet verantwoordelijk voor de dienstverlening, producten, installatie, facturatie, storingen, klachten of enige andere aspecten van Orange, Eneco of SmartSN (deze opsomming is niet-limitatief)</li>
                  </ul>
                </div>
                
                <p className="mt-4">
                  De algemene voorwaarden van Orange, Eneco en SmartSN zijn steeds van toepassing 
                  en raadpleegbaar via onderstaande links:
                </p>
                
                <div className="bg-slate-50 p-4 rounded-lg mt-4">
                  <p className="font-semibold mb-3">Algemene voorwaarden van onze partners:</p>
                  <ul className="space-y-2">
                    <li>
                      <a href="https://www.orange.be/nl/producten-en-diensten/algemene-voorwaarden" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                        Orange - Algemene voorwaarden
                      </a>
                    </li>
                    <li>
                      <a href="https://cdn.eneco.be/downloads/nl/general/vw/algemene-voorwaarden.pdf?_gl=1*1jocit9*_gcl_au*OTg5MDIxMjU4LjE3NzA5MTEyNDU.*FPAU*OTg5MDIxMjU4LjE3NzA5MTEyNDU." target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                        Eneco - Algemene voorwaarden (PDF)
                      </a>
                    </li>
                    <li>
                      <a href="https://smartsn.be/en/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                        SmartSN - Privacy Policy
                      </a>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 3 */}
            <Card>
              <CardHeader>
                <CardTitle>3. Welke gegevens verzamelen wij?</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Wij verzamelen enkel gegevens die noodzakelijk zijn om een prijsvoorstel op maat te kunnen maken:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <ul>
                    <li>• Naam</li>
                    <li>• Adres</li>
                    <li>• Postcode & gemeente</li>
                    <li>• Telefoonnummer</li>
                  </ul>
                  <ul>
                    <li>• E-mailadres</li>
                    <li>• Huidige provider</li>
                    <li>• Aantal gsm-nummers</li>
                    <li>• Gevraagde diensten</li>
                    <li>• Eventuele interesse in energie</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 4 */}
            <Card>
              <CardHeader>
                <CardTitle>4. Waarvoor gebruiken wij uw gegevens?</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Uw gegevens worden uitsluitend gebruikt voor:</p>
                <div className="space-y-2 mt-4">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Het maken van een persoonlijk prijsvoorstel</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Telefonisch contact voor advies</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Interne opvolging van uw aanvraag</span>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg mt-6">
                  <p className="font-semibold text-red-900">❌ Wij gebruiken uw gegevens NIET voor:</p>
                  <ul className="mt-2 text-red-800">
                    <li>• Massamarketing</li>
                    <li>• Doorverkoop</li>
                    <li>• Automatische advertenties</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 5 */}
            <Card>
              <CardHeader>
                <CardTitle>5. Doorgeven aan partners</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Uw gegevens worden <strong>niet</strong> doorgegeven aan Orange, Eneco of andere partners, tenzij:</p>
                <div className="space-y-2 mt-4">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <span>U expliciet ingaat op een voorstel</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <span>U zelf klant wenst te worden</span>
                  </div>
                </div>
                
                <p className="mt-4">
                  Pas dan worden noodzakelijke gegevens gedeeld om de aansluiting te realiseren.
                </p>
              </CardContent>
            </Card>

            {/* Section 6 */}
            <Card>
              <CardHeader>
                <CardTitle>6. Hoe lang bewaren wij uw gegevens?</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <div className="space-y-3">
                  <p>• Uw gegevens worden niet langer bewaard dan noodzakelijk</p>
                  <p>• Leads kunnen op elk moment manueel verwijderd worden</p>
                  <p>• Wij passen het principe van dataminimalisatie toe</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 7 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  7. Uw rechten (GDPR)
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>U heeft het recht om:</p>
                <div className="space-y-2 mt-4">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Uw gegevens in te kijken</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Uw gegevens te laten verbeteren</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <span>Uw gegevens volledig te laten verwijderen</span>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-6">
                  <p className="font-semibold text-blue-900">👉 Dit kan eenvoudig via:</p>
                  <div className="mt-2 text-blue-800">
                    <p>Ons <a href="/contact" className="underline font-medium">contactformulier</a></p>
                  </div>
                  <p className="mt-3 text-blue-800">Uw aanvraag wordt zo snel mogelijk verwerkt.</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 8 */}
            <Card>
              <CardHeader>
                <CardTitle>8. Beveiliging</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <p>Wij nemen technische en organisatorische maatregelen om uw gegevens te beschermen:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Beveiligde servers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Beperkte toegang</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Versleutelde communicatie</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Beveiligd dashboard</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 9 - Algemene Voorwaarden */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  9. Algemene Voorwaarden
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <p className="text-amber-900 font-medium mb-3">Belangrijke informatie:</p>
                  <ul className="text-amber-800 space-y-2">
                    <li>
                      De eigenschappen en diensten die worden voorgesteld door Telecomprijsvergelijker.be 
                      kunnen zonder voorafgaande mededeling gewijzigd worden door één van zijn partners.
                    </li>
                    <li>
                      De prijzen die door Telecomprijsvergelijker.be worden vermeld/gecommuniceerd zijn 
                      onder voorbehoud van wijzigingen, reken- en/of drukfouten, en zijn geldig zolang de voorraad strekt.
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 10 - Intellectuele Eigendom */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  10. Intellectuele Eigendom
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none">
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-red-900 font-medium mb-3">Auteursrecht en bescherming:</p>
                  <p className="text-red-800 mb-4">
                    Alle intellectuele eigendomsrechten met betrekking tot Telecomprijsvergelijker.be, 
                    inclusief maar niet beperkt tot het ontwerp, de werking, de structuur, de broncode, 
                    het vergelijkingssysteem, de algoritmes, de teksten, afbeeldingen, logo's en alle 
                    andere content, behoren exclusief toe aan SANDM EXPERTISE COMMV en VERCRUYSSE WIM.
                  </p>
                  <ul className="text-red-800 space-y-2">
                    <li>
                      <strong>Verbod op reproductie:</strong> Het is ten strengste verboden om enig onderdeel 
                      van deze website, het vergelijkingssysteem of de werking ervan te kopiëren, dupliceren, 
                      reproduceren, na te maken of te verveelvoudigen, in welke vorm dan ook.
                    </li>
                    <li>
                      <strong>Verbod op creatie van afgeleide werken:</strong> Het creëren van afgeleide 
                      werken, systemen of websites gebaseerd op het concept, de werking of het ontwerp 
                      van Telecomprijsvergelijker.be is niet toegestaan zonder uitdrukkelijke schriftelijke 
                      toestemming.
                    </li>
                    <li>
                      <strong>Reverse engineering:</strong> Het analyseren, decompileren of reverse engineeren 
                      van de technische werking van het vergelijkingssysteem is uitdrukkelijk verboden.
                    </li>
                  </ul>
                </div>
                <p className="mt-4 text-slate-600 text-sm">
                  © {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden.
                </p>
              </CardContent>
            </Card>

            {/* Section 11 - Contact (altijd onderaan) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  11. Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-4">
                  Voor vragen over privacy of gegevensbescherming kan u ons contacteren via het contactformulier.
                </p>
                
                <div className="flex gap-4 mt-6">
                  <Button onClick={() => navigate('/contact')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Contactformulier
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/')}>
                    Terug naar home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer - Same style as HomePage */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xl font-bold text-white mb-4">
                Telecomprijsvergelijker<span className="text-orange-500">.be</span>
              </div>
              <p className="text-sm mb-4">
                Wij helpen u besparen op TV, internet en mobiel door Telenet, Proximus en Base te vergelijken met Orange's aanbod.
              </p>
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 inline-flex">
                <img src={LOGOS.orange} alt="Orange" className="h-6" />
                <span className="text-sm text-white font-medium">Officieel Partner</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Links</h4>
              <ul className="space-y-2 text-sm">
                <li><span className="text-white">Privacybeleid en algemene voorwaarden</span></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookiebeleid</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contactformulier
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p className="mb-2">Officieel Orange partner via distributiekanaal SmartSN</p>
            <p>© {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;